import { createFileRoute, useSearch, useNavigate } from "@tanstack/react-router";
import { useEffect, useContext, useState, useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardContext } from "@/components/dashboard-layout";
import { CommonContactDetails } from "./-components/common-contact-details";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ServiceContactDetails } from "./-components/service-contact-details";
import { Filter } from "./-components/contact-list-toolbar";
import { CommonContactTable } from "./-components/common-contact-table";
import { ServiceContactTable } from "./-components/service-contact-table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ResponsiveActionBar, ResponsiveFormSection } from "@/components/form/responsive-form";

import {
  createCommonContactFn,
  updateCommonContactFn,
  createServiceContactFn,
  updateServiceContactFn,
  commonContactsQueryOptions,
  serviceContactsQueryOptions,
  deleteCommonContactFn,
  deleteServiceContactFn,
} from "@/server/organisations";
import { handleCloseParam } from "@/components/close-rightsidebar";
import { PlusIcon } from "lucide-react";
import { closeFullscreenDialog } from "@/components/fullscreen-sidebar";

export const Route = createFileRoute("/_dashboard/organisations/$orgId/contact-list")({
  component: RouteComponent,
  loader: async ({ params }: { params: { orgId: string } }) => {
    return {
      orgId: params.orgId,
      crumb: "Contact list",
    };
  },
});

function RouteComponent() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const context = useContext(DashboardContext);
  const queryClient = useQueryClient();
  const commonContactsQuery = useQuery(commonContactsQueryOptions(Number(orgId)));
  const serviceContactsQuery = useQuery(serviceContactsQueryOptions(Number(orgId)));
  const commonContacts = commonContactsQuery.data;
  const serviceContacts = serviceContactsQuery.data;
  const searchParams = useSearch({ strict: false }) as {
    search?: string;
    page?: string;
    perPage?: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    commonContactId?: string;
    serviceContactId?: string;
  };

  useEffect(() => {
    return () => {
      closeFullscreenDialog();
      context?.setRightSidebar(null);
    };
  }, []);

  const createCommonContactMutation = useMutation({
    mutationFn: createCommonContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commonContacts"] });
      closeAfterSave("commonContactId");
    },
  });

  const updateCommonContactMutation = useMutation({
    mutationFn: updateCommonContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commonContacts"] });
      closeAfterSave("commonContactId");
    },
  });

  const createServiceContactMutation = useMutation({
    mutationFn: createServiceContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceContacts"] });
      closeAfterSave("serviceContactId");
    },
  });

  const updateServiceContactMutation = useMutation({
    mutationFn: updateServiceContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceContacts"] });
      closeAfterSave("serviceContactId");
    },
  });

  const deleteCommonContactMutation = useMutation({
    mutationFn: deleteCommonContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["commonContacts"] });
      closeAfterSave("commonContactId");
    },
  });

  const deleteServiceContactMutation = useMutation({
    mutationFn: deleteServiceContactFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serviceContacts"] });
      closeAfterSave("serviceContactId");
    },
  });

  const [isContextReady, setIsContextReady] = useState(false);

  // When the page loads with the sidebar open, the context might not be ready and the sidebar will thus not be set.
  // This effect ensures that the sidebar is set when the context is ready.
  useEffect(() => {
    if (context) {
      setIsContextReady(true);
    }
  }, [context]);

  const search = searchParams.search ?? "";

  const handleFilterChange = useCallback(
    (filters: { search?: string }) => {
      navigate({
        to: "/organisations/$orgId/contact-list",
        params: { orgId },
        replace: true,
        search: (prev) => ({
          ...prev,
          search: filters.search?.trim() || undefined,
        }),
      });
    },
    [navigate, orgId]
  );

  const handleAddCommonContact = () => {
    if (!context) {
      return;
    }
    navigate({
      to: "/organisations/$orgId/contact-list",
      params: { orgId },
      replace: true,
      search: (prev) => ({ ...prev, commonContactId: "new", serviceContactId: undefined }),
    });
  };

  const handleAddServiceContact = () => {
    if (!context) {
      return;
    }
    navigate({
      to: "/organisations/$orgId/contact-list",
      params: { orgId },
      replace: true,
      search: (prev) => ({ ...prev, commonContactId: undefined, serviceContactId: "new" }),
    });
  };

  const saveCommonContact = useCallback(
    async (payload: any) => {
      const isNewContact = !payload.id;
      if (isNewContact) {
        try {
          const contactPromise = createCommonContactMutation.mutateAsync({
            data: {
              id: orgId,
              payload,
            },
          });
          toast.promise(contactPromise, {
            loading: "Creating common contact...",
            success: () => {
              return "Common contact created";
            },
            error: (error) => error.message || "Failed to create common contact",
          });
          return await contactPromise;
        } catch (error) {
          console.error(error);
          throw error;
        }
      } else {
        try {
          const contactPromise = updateCommonContactMutation.mutateAsync({ data: { orgId, payload } });
          toast.promise(contactPromise, {
            loading: "Updating common contact...",
            success: () => {
              return "Common contact updated";
            },
            error: (error) => error.message || "Failed to update common contact",
          });
          return await contactPromise;
        } catch (error) {
          console.error(error);
          throw error;
        }
      }
    },
    [orgId, createCommonContactMutation, updateCommonContactMutation]
  );

  const saveServiceContact = useCallback(
    async (values: any) => {
      const payload = {
        ...values,
        phonebookIndex: values.phonebookIndex ? Number(values.phonebookIndex) : undefined,
      };

      const isNewContact = !payload.id;
      if (isNewContact) {
        try {
          const contactPromise = createServiceContactMutation.mutateAsync({ data: { orgId, payload } });
          toast.promise(contactPromise, {
            loading: "Creating service contact...",
            success: () => {
              return "Service contact created";
            },
            error: (error) => error.message || "Failed to create service contact",
          });
          return await contactPromise;
        } catch (error) {
          console.error(error);
          throw error;
        }
      } else {
        try {
          const contactPromise = updateServiceContactMutation.mutateAsync({ data: { orgId, payload } });
          toast.promise(contactPromise, {
            loading: "Updating service contact...",
            success: () => {
              return "Service contact updated";
            },
            error: (error) => error.message || "Failed to update service contact",
          });
          return await contactPromise;
        } catch (error) {
          console.error(error);
          throw error;
        }
      }
    },
    [orgId, createServiceContactMutation, updateServiceContactMutation]
  );

  const deleteCommonContact = useCallback(
    async (contactId: number) => {
      try {
        const contactPromise = deleteCommonContactMutation.mutateAsync({
          data: { orgId, contactId: contactId.toString() },
        });
        toast.promise(contactPromise, {
          loading: "Deleting common contact...",
          success: () => {
            return "Common contact deleted";
          },
          error: (error) => error.message || "Failed to delete common contact",
        });
        return await contactPromise;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [orgId, deleteCommonContactMutation]
  );

  const deleteServiceContact = useCallback(
    async (contactId: number) => {
      try {
        const contactPromise = deleteServiceContactMutation.mutateAsync({
          data: { orgId, contactId: contactId.toString() },
        });
        toast.promise(contactPromise, {
          loading: "Deleting service contact...",
          success: () => {
            return "Service contact deleted";
          },
          error: (error) => error.message || "Failed to delete service contact",
        });
        return await contactPromise;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [orgId, deleteServiceContactMutation]
  );

  // Effect to handle sidebar updates when search params change
  useEffect(() => {
    if (!isContextReady || !context?.setRightSidebar) {
      return;
    }

    // Determine which sidebar to show based on search params
    if (searchParams.commonContactId !== undefined && searchParams.commonContactId !== null) {
      if (searchParams.commonContactId === "new") {
        context.setRightSidebar(
          <CommonContactDetails
            commonContact={null}
            saveCommonContact={saveCommonContact}
            deleteContact={() => {}}
            close={() => closeAfterSave("commonContactId")}
          />
        );
      } else {
        const contact = commonContacts?.find((c) => c.id?.toString() == searchParams.commonContactId);
        if (contact) {
          context.setRightSidebar(
            <CommonContactDetails
              commonContact={contact}
              saveCommonContact={saveCommonContact}
              deleteContact={deleteCommonContact}
              close={() => closeAfterSave("commonContactId")}
            />
          );
        }
      }
      context.setRightSidebarParamKey?.("commonContactId");
    } else if (searchParams.serviceContactId !== undefined && searchParams.serviceContactId !== null) {
      if (searchParams.serviceContactId === "new") {
        context.setRightSidebar(
          <ServiceContactDetails
            serviceContact={null}
            saveServiceContact={saveServiceContact}
            deleteContact={() => {}}
            close={() => closeAfterSave("serviceContactId")}
          />
        );
      } else {
        const contact = serviceContacts?.find((c) => c.id?.toString() == searchParams.serviceContactId);
        if (contact) {
          context.setRightSidebar(
            <ServiceContactDetails
              serviceContact={contact}
              saveServiceContact={saveServiceContact}
              deleteContact={deleteServiceContact}
              close={() => closeAfterSave("serviceContactId")}
            />
          );
        }
      }
      context.setRightSidebarParamKey?.("serviceContactId");
    } else {
      // Clear sidebar if no contact is selected
      context.setRightSidebar(null);
    }
  }, [isContextReady, searchParams.commonContactId, searchParams.serviceContactId, commonContacts, serviceContacts]);

  if (!context) {
    return <div>Loading...</div>;
  }

  const closeAfterSave = (contactIdType: "commonContactId" | "serviceContactId") => {
    closeFullscreenDialog();
    context.setRightSidebar(null);
    navigate({
      to: "/organisations/$orgId/contact-list",
      params: { orgId },
      replace: true,
      search: (prev) => {
        const next = { ...(prev || {}) } as Record<string, unknown>;
        delete next[contactIdType];
        return Object.keys(next).length > 0 ? next : undefined;
      },
    });
  };

  return (
    <ResponsiveFormSection className="flex flex-col gap-4">
      <Filter onFilterChange={handleFilterChange} defaultSearch={search} />
      <Card>
        <CardHeader className="flex justify-between min-h-[36px]">
          <CardTitle>Common contacts</CardTitle>
          {searchParams.commonContactId !== "new" && (
            <Button variant="outline" onClick={handleAddCommonContact}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add common contact
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <CommonContactTable commonContacts={commonContacts || []} orgId={orgId} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex justify-between min-h-[36px]">
          <CardTitle>Service contacts</CardTitle>
          {searchParams.serviceContactId !== "new" && (
            <Button variant="outline" onClick={handleAddServiceContact}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add service contact
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <ServiceContactTable serviceContacts={serviceContacts || []} orgId={orgId} />
        </CardContent>
      </Card>
    </ResponsiveFormSection>
  );
}
